import { createContext, useState, ReactNode } from "react";
import {
  ConfirmationModal,
  ConfirmationModalConfig,
} from "components/ConfirmationModal/ConfirmationModal.component";

interface ConfirmationModalContextProps {
  setModalConfig: React.Dispatch<
    React.SetStateAction<Omit<ConfirmationModalConfig, "setOpen" | "isLoading" | "error"> | null>
  >;
}

export const ConfirmationModalContext = createContext<
  ConfirmationModalContextProps | undefined
>(undefined);

export const ConfirmationModalProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [modalConfig, setModalConfig] = useState<Omit<
    ConfirmationModalConfig,
    "setOpen" | "isLoading" | "error"
  > | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setModalConfig(null);
    setIsLoading(false);
    setError(null);
  };

  const handleApprove = async () => {
    if (!modalConfig) return;

    setIsLoading(true);
    setError(null);

    try {
      await modalConfig.onApprove(modalConfig.params);
      handleClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : "An error occurred. Please try again.";
      setError(errorMessage);
      setIsLoading(false);
    }
  };

  return (
    <ConfirmationModalContext.Provider value={{ setModalConfig }}>
      {children}
      {modalConfig && (
        <ConfirmationModal
          isOpen={modalConfig.isOpen}
          title={modalConfig.title}
          description={modalConfig.description}
          buttonText={modalConfig.buttonText}
          buttonTextCancel={modalConfig.buttonTextCancel}
          onApprove={handleApprove}
          setOpen={() => handleClose()}
          hideCancelButton={modalConfig.hideCancelButton}
          isLoading={isLoading}
          error={error}
        />
      )}
    </ConfirmationModalContext.Provider>
  );
};
